import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Modal } from "flowbite-react";
import {
  HiEye,
  HiFilter,
  HiOutlineArrowRight,
  HiOutlineArrowSmDown,
  HiOutlineArrowSmUp,
  HiOutlineEye,
  HiOutlineTrash,
  HiPlus,
} from "react-icons/hi";
import { IoMdPerson, IoMdSearch } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import AnimatedToast from "../components/common/AnimatedToast";
import axiosInstance from "../config/axiosInstance";
import { BUDGETS } from "../config/apiConfig";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import { useApplication } from "../contexts/ApplicationContext";

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);

const formatCurrencyFromDigits = (digits) => {
  if (!digits) return "";
  const numeric = Number(digits);
  if (!Number.isFinite(numeric)) return "";
  return formatCurrency(numeric / 100);
};

const parseCurrencyDigits = (digits) => {
  if (!digits) return null;
  const numeric = Number(digits);
  if (!Number.isFinite(numeric)) return null;
  return numeric / 100;
};

const formatRelative = (value) => {
  if (!value) return "-";
  return moment(value).locale("pt-br").fromNow();
};

const getValidityState = (validUntil) => {
  if (!validUntil) {
    return { label: "Sem validade", color: "gray" };
  }
  const isValid = moment(validUntil).isAfter(moment());
  return isValid
    ? { label: "Em vigor", color: "success" }
    : { label: "Expirado", color: "failure" };
};

const DEFAULT_PAGE_LIMIT = 20;
const getInitialSortingState = () => ({
  orderBy: "createdAt",
  order: "desc",
});

const BudgetListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userPermission } = useApplication();
  const [budgets, setBudgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [templateMap, setTemplateMap] = useState({});
  const initialFilterState = {
    search: "",
    template: "",
    createdFrom: "",
    createdTo: "",
    valueMin: "",
    valueMax: "",
  };
  const [filters, setFilters] = useState(initialFilterState);
  const [filterDraft, setFilterDraft] = useState(initialFilterState);
  const [sorting, setSorting] = useState(getInitialSortingState);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_PAGE_LIMIT,
    total: 0,
    totalPages: 1,
    offset: 0,
  });
  const resetFilters = () => {
    const fresh = { ...initialFilterState };
    setFilters(fresh);
    setFilterDraft(fresh);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  const handleCurrencyInput = (field, rawValue) => {
    const digits = String(rawValue).replace(/[^\d]/g, "");
    setFilterDraft((prev) => ({ ...prev, [field]: digits }));
  };
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [isDeletingBudget, setIsDeletingBudget] = useState(false);
  const [deletingBudgetId, setDeletingBudgetId] = useState(null);
  const [budgetMenuOpen, setBudgetMenuOpen] = useState(false);
  const [activeBudget, setActiveBudget] = useState(null);
  const [menuSlideIn, setMenuSlideIn] = useState(false);
  const applyFilters = () => {
    const applied = { ...filterDraft };
    setFilters(applied);
    setFilterDraft(applied);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  const handleSortChange = (field) => {
    setSorting((prev) => {
      const isSameField = prev.orderBy === field;
      const nextOrder = isSameField
        ? prev.order === "asc"
          ? "desc"
          : "asc"
        : "asc";
      return { orderBy: field, order: nextOrder };
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  const handleChangePage = (newPage) => {
    setPagination((prev) => {
      const lastPage = Math.max(prev.totalPages || 1, 1);
      const normalizedPage = Math.min(Math.max(newPage, 1), lastPage);
      return { ...prev, page: normalizedPage };
    });
  };
  const openBudgetMenu = (budget) => {
    setActiveBudget(budget);
    setBudgetMenuOpen(true);
  };
  const closeBudgetMenu = () => {
    setBudgetMenuOpen(false);
    setActiveBudget(null);
  };
  const handleDeleteBudget = async () => {
    if (!activeBudget) return;
    setIsDeletingBudget(true);
    try {
      await axiosInstance.delete(BUDGETS.GET(activeBudget.id));
      setAnimatedToast({
        id: Date.now(),
        message: "Orçamento excluído.",
      });
      await loadBudgets();
      closeBudgetMenu();
    } catch (deleteError) {
      console.error("[BudgetListPage] erro ao excluir orçamento", deleteError);
      setError(
        deleteError?.response?.data?.message ||
          "Não foi possível excluir o orçamento."
      );
    } finally {
      setIsDeletingBudget(false);
    }
  };
  const handleDeleteBudgetRow = async (budgetId) => {
    if (!budgetId || deletingBudgetId === budgetId) return;
    if (!window.confirm("Deseja remover este orçamento?")) return;
    setDeletingBudgetId(budgetId);
    try {
      await axiosInstance.delete(BUDGETS.GET(budgetId));
      setAnimatedToast({
        id: Date.now(),
        message: "Orçamento excluído.",
      });
      await loadBudgets();
    } catch (deleteError) {
      console.error("[BudgetListPage] erro ao excluir orçamento", deleteError);
      setError(
        deleteError?.response?.data?.message ||
          "Não foi possível excluir o orçamento."
      );
    } finally {
      setDeletingBudgetId(null);
    }
  };
  useEffect(() => {
    if (!budgetMenuOpen) {
      setMenuSlideIn(false);
      return;
    }
    requestAnimationFrame(() => {
      setMenuSlideIn(true);
    });
  }, [budgetMenuOpen]);

  const [animatedToast, setAnimatedToast] = useState(null);

  const loadBudgets = useCallback(async () => {
    setError("");
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.createdFrom) params.set("createdFrom", filters.createdFrom);
      if (filters.createdTo) params.set("createdTo", filters.createdTo);
      if (filters.search) params.set("search", filters.search);
      if (filters.template) params.set("templateId", filters.template);
      const parsedMin = parseCurrencyDigits(filters.valueMin);
      const parsedMax = parseCurrencyDigits(filters.valueMax);
      if (parsedMin !== null) params.set("valueMin", parsedMin);
      if (parsedMax !== null) params.set("valueMax", parsedMax);
      if (String(userPermission || "").toUpperCase() === "USER" && user?.id) {
        params.set("createdBy", String(user.id));
      }
      params.set("page", String(pagination.page));
      params.set("limit", String(pagination.limit));
      if (sorting.orderBy) {
        params.set("orderBy", sorting.orderBy);
        params.set("order", sorting.order);
      }
      const url =
        params.toString().length > 0
          ? `${BUDGETS.LIST}?${params.toString()}`
          : BUDGETS.LIST;
      const response = await axiosInstance.get(url);
      const payload = response.data?.data ?? {};
      const items = payload?.budgets ?? [];
      setBudgets(items);
      const paginationInfo = payload?.pagination ?? {};
      setPagination((prev) => ({
        ...prev,
        total: paginationInfo.total ?? prev.total,
        totalPages: paginationInfo.totalPages ?? prev.totalPages,
        limit: paginationInfo.limit ?? prev.limit,
        offset: paginationInfo.offset ?? prev.offset,
        page: paginationInfo.page ?? prev.page,
      }));
    } catch (fetchError) {
      console.error("[BudgetListPage] falha ao buscar orçamentos", fetchError);
      setError(
        fetchError?.response?.data?.message ||
          "Não foi possível carregar os orçamentos."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    filters.createdFrom,
    filters.createdTo,
    filters.search,
    filters.template,
    filters.valueMin,
    filters.valueMax,
    userPermission,
    user?.id,
    sorting.order,
    sorting.orderBy,
    pagination.page,
    pagination.limit,
  ]);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templateResponse = await axiosInstance.get(BUDGETS.TEMPLATES);
        const templates = Array.isArray(templateResponse.data?.data)
          ? templateResponse.data.data
          : [];
        setTemplateMap(
          templates.reduce((acc, template) => {
            acc[template.id] = template.label || template.id;
            return acc;
          }, {})
        );
      } catch (templateError) {
        console.error(
          "[BudgetListPage] falha ao carregar templates",
          templateError
        );
      }
    };

    loadTemplates();
  }, []);

  useEffect(() => {
    const pendingToast = location.state?.toast;
    if (pendingToast) {
      setAnimatedToast(pendingToast);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const skeletonRows = Array.from({ length: 10 });
  const SortableHeader = ({ field, label, align = "left" }) => {
    const isActive = sorting.orderBy === field;
    const justifyClass =
      align === "right"
        ? "justify-end"
        : align === "center"
        ? "justify-center"
        : "justify-start";
    const textAlignClass =
      align === "right"
        ? "text-right"
        : align === "center"
        ? "text-center"
        : "text-left";
    const iconClass = (direction) =>
      isActive && sorting.order === direction
        ? "text-orange-500"
        : "text-slate-400";

    return (
      <th className={`px-4 py-3 ${textAlignClass}`}>
        <button
          type="button"
          className={`inline-flex w-full items-center gap-1 ${justifyClass}`}
          onClick={() => handleSortChange(field)}
          aria-pressed={isActive}
        >
          <span
            className={`text-[11px] uppercase tracking-[0.3em] ${
              isActive ? "text-slate-900" : "text-slate-500"
            }`}
          >
            {label}
          </span>
          <span className="flex text-[13px] leading-none">
            <HiOutlineArrowSmUp className={`${iconClass("asc")} -mr-1`} />
            <HiOutlineArrowSmDown className={iconClass("desc")} />
          </span>
        </button>
      </th>
    );
  };
  const limitForRange = pagination.limit || DEFAULT_PAGE_LIMIT;
  const offsetForRange =
    pagination.offset !== undefined
      ? pagination.offset
      : (pagination.page - 1) * limitForRange;
  const totalItems = pagination.total ?? 0;
  const rangeStart = totalItems > 0 ? offsetForRange + 1 : 0;
  const rangeEnd =
    totalItems > 0 ? Math.min(offsetForRange + limitForRange, totalItems) : 0;
  const canPrevious = pagination.page > 1;
  const canNext = pagination.page < Math.max(pagination.totalPages || 1, 1);

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-1">
        <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
          Orçamentos
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Histórico de orçamentos
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="hidden md:flex items-center gap-2"
              onClick={() => navigate("/budget")}
            >
              Novo orçamento
            </Button>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-gray-200 bg-slate-50 shadow-sm">
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1">
              <div className="relative">
                <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 pl-10 text-sm focus:border-gray-300 focus:ring-0"
                  placeholder="Buscar por cliente, referência ou template"
                  value={filterDraft.search}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({
                      ...prev,
                      search: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button
                color="light"
                className="text-sm hidden md:block"
                onClick={resetFilters}
              >
                Limpar
              </Button>
              <Button
                size="sm"
                className="text-sm flex items-center gap-1 md:hidden bg-gray-500 text-white"
                onClick={() => setFiltersModalOpen(true)}
              >
                <HiFilter className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                className="text-xs block md:hidden py-0"
                onClick={applyFilters}
              >
                Filtrar
              </Button>
            </div>
          </div>
          <div className="mt-3 hidden md:flex md:grid-cols-4 gap-3">
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Data inicial
              </label>
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.createdFrom}
                onChange={(e) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    createdFrom: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Data final
              </label>
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.createdTo}
                onChange={(e) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    createdTo: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Valor mínimo
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                placeholder="R$ 0,00"
                value={formatCurrencyFromDigits(filterDraft.valueMin)}
                onChange={(e) =>
                  handleCurrencyInput("valueMin", e.target.value)
                }
              />
            </div>
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Valor máximo
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                placeholder="R$ 0,00"
                value={formatCurrencyFromDigits(filterDraft.valueMax)}
                onChange={(e) =>
                  handleCurrencyInput("valueMax", e.target.value)
                }
              />
            </div>
          </div>

          <div className="mt-3 justify-end hidden md:flex">
            <Button className="text-sm" onClick={applyFilters}>
              Filtrar
            </Button>
          </div>
        </div>
      </div>
      <Modal
        show={filtersModalOpen}
        size="lg"
        popup
        onClose={() => setFiltersModalOpen(false)}
      >
        <Modal.Header>Filtros</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Data inicial
              </label>
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.createdFrom}
                onChange={(e) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    createdFrom: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Data final
              </label>
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.createdTo}
                onChange={(e) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    createdTo: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Valor mínimo
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                placeholder="R$ 0,00"
                value={formatCurrencyFromDigits(filterDraft.valueMin)}
                onChange={(e) =>
                  handleCurrencyInput("valueMin", e.target.value)
                }
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Valor máximo
              </label>
              <input
                type="text"
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                placeholder="R$ 0,00"
                value={formatCurrencyFromDigits(filterDraft.valueMax)}
                onChange={(e) =>
                  handleCurrencyInput("valueMax", e.target.value)
                }
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.3em] text-slate-400">
                Template
              </label>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                value={filterDraft.template}
                onChange={(e) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    template: e.target.value,
                  }))
                }
              >
                <option value="">Todos</option>
                {Object.entries(templateMap).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-between">
          <Button color="light" onClick={resetFilters}>
            Limpar
          </Button>
          <Button
            color="success"
            onClick={() => {
              applyFilters();
              setFiltersModalOpen(false);
            }}
          >
            Aplicar
          </Button>
        </Modal.Footer>
      </Modal>
      {budgetMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black opacity-50"
            onClick={closeBudgetMenu}
          />
          <div
            className={`relative w-full rounded-t-xl bg-white px-6 pt-3 pb-8 shadow-[0_0_24px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-out transform ${
              menuSlideIn ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-slate-200"></div>
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Opções rápidas
              </p>
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-900">
                  {activeBudget?.reference || "Orçamento"}
                </p>
                <p className="text-sm text-slate-500">
                  {activeBudget?.clientName || activeBudget?.client || "-"}
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  color="success"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={() => {
                    if (!activeBudget) return;
                    navigate(
                      `/budget?budgetId=${encodeURIComponent(activeBudget.id)}`
                    );
                    closeBudgetMenu();
                  }}
                >
                  Visualizar orçamento
                </Button>
                <Button
                  color="failure"
                  fullWidth
                  className="rounded-2xl py-3 text-base font-semibold w-full"
                  onClick={handleDeleteBudget}
                  isProcessing={isDeletingBudget}
                >
                  Excluir orçamento
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        {error && (
          <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700">
            {error}
          </div>
        )}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm text-slate-700">
            <thead className="text-xs uppercase tracking-[0.3em] text-slate-500">
              <tr>
                <SortableHeader field="reference" label="Referência" />
                <SortableHeader field="client" label="Cliente" />
                <SortableHeader field="createdAt" label="Criação" />
                <SortableHeader field="validUntil" label="Validade" />
                <SortableHeader
                  field="totalValue"
                  label="Total"
                  align="right"
                />
                <th className="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                skeletonRows.map((_, index) => (
                  <tr
                    key={`skeleton-${index}`}
                    className="border-t border-slate-100 last:border-b"
                  >
                    <td className="px-4 py-4">
                      <div className="h-4 w-3/5 rounded-full bg-slate-200 animate-pulse" />
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-4 w-4/5 rounded-full bg-slate-200 animate-pulse" />
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-4 w-2/3 rounded-full bg-slate-200 animate-pulse" />
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-4 w-2/3 rounded-full bg-slate-200 animate-pulse" />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="inline-block h-4 w-16 rounded-full bg-slate-200 animate-pulse" />
                    </td>
                    <td className="px-4 py-4">
                      <div className="mx-auto h-8 w-14 rounded-full bg-slate-200 animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : budgets.length ? (
                budgets.map((budget) => {
                  const createdAt = budget.createdAt
                    ? moment(budget.createdAt).format("DD/MM/yyyy")
                    : "-";
                  const validity = budget.validUntil
                    ? moment(budget.validUntil).format("DD/MM/yyyy")
                    : "-";
                  const totalValue = Number(
                    budget.totalValue ?? budget.total ?? 0
                  );
                  const templateLabel =
                    templateMap[budget.templateId] || budget.templateId || "-";
                  return (
                    <tr
                      key={budget.id}
                      className="border-t border-slate-100 last:border-b"
                    >
                      <td className="px-4 py-4 font-semibold text-slate-900">
                        {budget.reference}
                      </td>
                      <td className="px-4 py-4">
                        {budget.clientName || budget.client || "-"}
                      </td>
                      <td className="px-4 py-4  gap-1">
                        <div className="flex flex-col">
                          <span>{createdAt}</span>
                          <span className="text-xs text-slate-400">
                            {formatRelative(budget.createdAt)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4  gap-1">
                        <div className="flex flex-col">
                          <span>{validity}</span>
                          <Badge
                            color={getValidityState(budget.validUntil).color}
                            className="w-fit"
                          >
                            {getValidityState(budget.validUntil).label}
                          </Badge>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-right font-semibold text-slate-900">
                        {formatCurrency(totalValue)}
                      </td>
                      <td className="px-4 py-4 text-center flex justify-center gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/budget?budgetId=${encodeURIComponent(
                                budget.id
                              )}`
                            )
                          }
                          className="flex items-center gap-1"
                        >
                          <div className="flex items-center gap-2">
                            <HiOutlineEye />
                          </div>
                        </Button>
                        <Button
                          size="sm"
                          color="failure"
                          disabled={deletingBudgetId === budget.id}
                          onClick={() => handleDeleteBudgetRow(budget.id)}
                          className="flex items-center gap-1"
                        >
                          <HiOutlineTrash />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-sm text-slate-500"
                  >
                    Nenhum orçamento encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 px-3 py-2 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
          <div>
            {totalItems
              ? `Mostrando ${rangeStart}-${rangeEnd} de ${totalItems}`
              : "Nenhum orçamento encontrado."}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              color="light"
              onClick={() => handleChangePage(pagination.page - 1)}
              disabled={!canPrevious}
            >
              Anterior
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => handleChangePage(pagination.page + 1)}
              disabled={!canNext}
            >
              Próximo
            </Button>
          </div>
        </div>
        <div className="grid gap-3 md:hidden">
          {isLoading ? (
            skeletonRows.map((_, index) => (
              <div
                key={`card-skeleton-${index}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow"
              >
                <div className="h-4 w-2/3 rounded-full bg-slate-200 animate-pulse mb-2" />
                <div className="h-3 w-1/2 rounded-full bg-slate-200 animate-pulse mb-4" />
                <div className="space-y-2">
                  {[...Array(3)].map((__, idx) => (
                    <div
                      key={idx}
                      className="h-3 w-full rounded-full bg-slate-200 animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))
          ) : budgets.length ? (
            budgets.map((budget) => {
              const createdAt = budget.createdAt
                ? moment(budget.createdAt).format("DD/MM/yyyy")
                : "-";
              const validity = budget.validUntil
                ? moment(budget.validUntil).format("DD/MM/yyyy")
                : "-";
              const totalValue = Number(budget.totalValue ?? budget.total ?? 0);
              const templateLabel =
                templateMap[budget.templateId] || budget.templateId || "-";
              return (
                <div
                  key={`card-${budget.id}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow cursor-pointer transition hover:shadow-lg"
                  role="button"
                  tabIndex={0}
                  onClick={() => openBudgetMenu(budget)}
                >
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Criado: {createdAt}</span>
                    <span>Válido: {validity}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                    <span>{formatRelative(budget.createdAt)}</span>
                    <Badge color={getValidityState(budget.validUntil).color}>
                      {getValidityState(budget.validUntil).label}
                    </Badge>
                  </div>
                  <div className="mt-2 mb-2 text-base font-semibold text-slate-900">
                    {budget.reference}
                  </div>
                  <div className="text-sm text-slate-600">
                    <p className="flex items-center gap-1">
                      <IoMdPerson />
                      <span>{budget.clientName || budget.client || "-"}</span>
                    </p>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">
                      {formatCurrency(totalValue)}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center text-sm text-slate-500 shadow-sm">
              Nenhum orçamento encontrado.
            </div>
          )}
        </div>
      </div>
      <AnimatedToast
        open={Boolean(animatedToast)}
        message={animatedToast?.message}
        onClose={() => setAnimatedToast(null)}
      />
      <div className="fixed bottom-4 right-4 z-40 md:hidden">
        <Button
          size="lg"
          className="h-14 w-14 rounded-full shadow-xl border-0 flex items-center justify-center"
          onClick={() => navigate("/budget")}
          aria-label="Novo orçamento"
        >
          <HiPlus className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default BudgetListPage;
